<template>
    <div class="q-mt-md q-gutter-y-md">
        <q-card>
            <q-tabs
                v-model="activeTab"
                class="primary bg-grey-3"
                active-color="primary"
                indicator-color="primary"
                align="justify"
            >
                <q-tab
                    :label="$t('Product Categories')"
                    name="productCategories"
                />
                <q-tab
                    :label="$t('Product Properties')"
                    name="productProperties"
                />
            </q-tabs>
            <q-separator></q-separator>
            <q-tab-panels v-model="activeTab" animated keep-alive>
                <q-tab-panel name="productCategories" class="tab-panel">
                    <q-table
                        :title="$t('Product Categories')"
                        :rows="categories"
                        :columns="tableColumns"
                        :grid="$q.screen.lt.md"
                        :filter="search"
                        v-model:pagination="pagination"
                        :rows-per-page-options="[pagination.rowsPerPage]"
                        :no-data-label="
                            $t(
                                'No product categories created yet. Click on Create Product Category button to add a new one.'
                            )
                        "
                        row-key="name"
                        table-style="max-height: 100%;"
                        binary-state-sort
                        flat
                    >
                        <!-- top bar -->
                        <template v-slot:top>
                            <!-- table title -->
                            <div class="col-12">
                                <div class="text-h4 text-weight-medium">
                                    {{ $t('Product Categories') }}
                                </div>
                                <div class="page-description">
                                    {{
                                        $t(
                                            'Create and modify products categories.'
                                        )
                                    }}
                                </div>
                            </div>
                            <div
                                class="row fit"
                                :class="
                                    $q.screen.lt.sm
                                        ? ''
                                        : 'wrap justify-between items-start content-start'
                                "
                            >
                                <!-- search bar -->
                                <q-input
                                    v-model="search"
                                    @clear="search = ''"
                                    :placeholder="
                                        $t('Search product categories')
                                    "
                                    :class="
                                        $q.screen.lt.sm
                                            ? 'col-12 q-mb-md'
                                            : 'col q-mr-md'
                                    "
                                    debounce="750"
                                    clearable
                                    outlined
                                    dense
                                    for="search-product-category-input"
                                    color="accent"
                                >
                                    <template v-slot:prepend>
                                        <q-icon name="fas fa-search" />
                                    </template>
                                </q-input>

                                <!-- Create product category button -->
                                <q-btn
                                    v-show="isUserAuthorized"
                                    @click="
                                        $router.push({
                                            name: 'product_category_detail'
                                        })
                                    "
                                    :label="$t('Create Product Category')"
                                    class="table-header__button"
                                    :class="$q.screen.lt.sm ? 'col-12' : ''"
                                    color="accent"
                                    size="md"
                                ></q-btn>
                            </div>
                        </template>
                        <!-- top bar ends here -->
                        <!-- table body -->
                        <template v-slot:body="props">
                            <q-tr :props="props">
                                <q-td key="name" :props="props">
                                    {{ props.row.name }}
                                </q-td>
                                <q-td key="actions" :props="props">
                                    <div class="row q-gutter-md">
                                        <!-- "edit user" button -->
                                        <q-btn
                                            :disable="!isUserAuthorized"
                                            @click="edit(props.row)"
                                            class="table__action-button edit-user-btn"
                                            color="accent"
                                            icon="fas fa-edit"
                                            outline
                                        >
                                            <q-tooltip v-if="!isUserAuthorized">
                                                {{
                                                    $t(
                                                        'You are not authorized to edit this product category'
                                                    )
                                                }}
                                            </q-tooltip>
                                        </q-btn>
                                        <!-- "remove product category" button -->
                                        <q-btn
                                            :disable="!isUserAuthorized"
                                            @click="remove(props.row)"
                                            class="table__action-button"
                                            color="red"
                                            icon="fas fa-trash-alt"
                                            outline
                                        >
                                            <q-tooltip v-if="!isUserAuthorized">
                                                {{
                                                    $t(
                                                        'You are not authorized to delete this product category'
                                                    )
                                                }}
                                            </q-tooltip>
                                        </q-btn>
                                    </div>
                                </q-td>
                            </q-tr>
                        </template>
                        <template v-slot:item="props">
                            <div
                                class="q-pa-md col-xs-12 col-sm-6 col-md-4 col-lg-3 grid-style-transition"
                            >
                                <q-card>
                                    <q-list dense>
                                        <q-item>
                                            <q-item-section>
                                                <q-item-label>
                                                    <span
                                                        class="q-mt-sm q-mr-md"
                                                    >
                                                        <strong>
                                                            {{
                                                                $t('Name')
                                                            }}:</strong
                                                        >
                                                    </span>
                                                    <span>
                                                        {{ props.row.name }}
                                                    </span>
                                                </q-item-label>
                                            </q-item-section>
                                        </q-item>
                                    </q-list>
                                    <q-card-section>
                                        <q-btn
                                            :disable="!isUserAuthorized"
                                            @click="edit(props.row)"
                                            class="q-mr-sm"
                                            color="accent"
                                            icon="fas fa-edit"
                                            outline
                                        ></q-btn>
                                        <q-btn
                                            :disable="!isUserAuthorized"
                                            @click="remove(props.row)"
                                            color="red"
                                            icon="fas fa-trash-alt"
                                            outline
                                        ></q-btn>
                                    </q-card-section>
                                </q-card>
                            </div>
                        </template>
                    </q-table>
                </q-tab-panel>
                <q-tab-panel name="productProperties">
                    <ProductProperties />
                </q-tab-panel>
            </q-tab-panels>
        </q-card>
    </div>
</template>
<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import ProductProperties from '@pages/ProductProperties.vue';
import { useSharedStore } from '@stores/shared.js';
import { mapState, mapActions } from 'pinia';
import { useProductsStore } from '@stores/products.js';

export default {
    name: 'ProductCategories',
    mixins: [BreadcrumbsManager],
    components: { ProductProperties },
    created() {
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.$t('Global Settings'),
                route: {
                    name: 'settings'
                }
            },
            {
                label: this.$t('Product Categories'),
                route: {
                    name: 'product_categories'
                }
            }
        ]);
        this.pagination.rowsNumber = this.categories.length;
    },
    /**
     * Navigation guard that is called before the route is entered.
     * If navigating from product property detail page, set active tab to product properties.
     * @param {Object} to - The target Route Object being navigated to.
     * @param {Object} from - The current route being navigated away from.
     * @param {Function} next - This function must be called to resolve the hook.
     */
    beforeRouteEnter(to, from, next) {
        next(vm => {
            if (from.name === 'product_property_detail') {
                vm.activeTab = 'productProperties';
            }
        });
    },
    computed: {
        ...mapState(useProductsStore, ['getProductCategories']),
        /**
         * Filter product categories
         * @returns {Array} filtered product categories
         */
        categories() {
            return this.getProductCategories.filter(category =>
                category.name.toLowerCase().includes(this.search.toLowerCase())
            );
        },
        /**
         * Check if user is authorized to create, edit or delete a product category
         * @returns {Boolean} is user authorized
         */
        isUserAuthorized() {
            return this.$isUserAdmin() || this.$isUserManager();
        }
    },
    data() {
        return {
            activeTab: 'productCategories',
            pagination: {
                sortBy: '',
                descending: false,
                page: 1,
                rowsPerPage: 30,
                rowsNumber: null
            },
            search: '',
            tableColumns: [
                {
                    name: 'name',
                    align: 'left',
                    label: 'Name',
                    field: 'name',
                    sortable: true
                },
                {
                    name: 'actions',
                    label: this.$t('Actions'),
                    field: 'actions',
                    align: 'left'
                }
            ]
        };
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useProductsStore, ['removeProductCategoryAction']),
        /**
         * Edit a product category
         * @param {Object} row
         */
        edit(row) {
            this.$router.push({
                name: 'product_category_detail',
                state: { id: row.id, name: row.name }
            });
        },
        /**
         * Remove a product category
         * @param {Object} row
         */
        async remove(row) {
            try {
                // begin loading
                this.beginLoading(`${this.$t('Removing product category')}...`);
                await this.removeProductCategoryAction(row);
            } catch (error) {
                this.$notifyError("$t('Failed to remove product category')");
            } finally {
                // end loading
                this.stopLoading();
            }
        }
    }
};
</script>
